import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { HOLIDAY_REGIONS, addDays, addMonths, isIsoDate, isMonthKey } from "@talentkontor/domain";
import { useStaffWorkspace, useTalentWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { Dialog, PillView, useAction } from "../../../components/ui";
import { date, today as todayIn } from "../../../lib/format";
import { useViewerZone, zoneHint } from "../../../lib/zeitzone";
import { useLang } from "../../creator/sprache";
import { dateOf, type Lang } from "../../creator/lang";
import { aboBeenden, aboErzeugen, aboStand, ladeKalender, ladeTerminDatum, speichereFeiertagsregion } from "./daten";
import {
  ART_FILTER, abfrageBereich, amTag, anstehend, antwortPille, baueEintraege, eintragTitel, farbWert, fasseZusammen, filtere, hinweisZeile, imMonat, jahreFuer,
  listenPille, lokaleTage, monatTitel, monatsRaster, nachschlag, neuSeit, pilleFuer, tagLang, talentVoll, verantwortlich, zeitText, zielgruppeText,
  WOCHENTAGE, WOCHENTAGE_EN, type ArtFilter, type Eintrag, type Perspektive,
} from "./model";
import { KalenderDialoge, type DialogZustand, type KalenderKontext } from "./dialoge";
import { DemoHinweis, gespeichert, speichere } from "./felder";
import "./kalender.css";
import { t, ta } from "../../../lib/sprache";
import { kalenderMerkKey } from "./neu";

/**
 * Kalender der Agentur (11_app_agentur/kalender.html) und des Creators (12_app_creator/kalender.html).
 * Umfang wie im bisherigen Portal (AD-187 … AD-191, TA-120 … TA-126): Monat und Liste, vor/zurück, Heute, Filter nach Creator
 * und Art, ausgeblendete automatische Termine, Termine anlegen/ändern/löschen, Zielgruppen und Gruppen, Benachrichtigen,
 * automatische Termine anpassen oder ausblenden, Feiertage der Agentur-Region, Uhrzeiten in der eigenen Zeitzone.
 * Sprunglinks: ?monat=YYYY-MM, ?datum=YYYY-MM-DD, ?termin=<id> (Glocke und Mail), ?ansicht=liste.
 */

export function KalenderAgentur() {
  const ws = useStaffWorkspace();
  const eingeschraenkt = !ws.isChef && !ws.membership.sees_all_talents;
  return (
    <KalenderSeite key={ws.org.org_id} perspektive="agentur" orgId={ws.org.org_id} orgZone={ws.org.timezone} eigeneTalentId={null}
      merkKey={kalenderMerkKey(ws.membership.id)} seesAll={ws.isChef || ws.membership.sees_all_talents} einzeln={!!ws.isCreator}
      hinweis={eingeschraenkt ? t("Du siehst Termine und Fristen deiner zugewiesenen Creator sowie Termine für alle und das Team.", "You see appointments and deadlines of your assigned creators as well as appointments for everyone and the team.") : null} />
  );
}

export function KalenderCreator() {
  const ws = useTalentWorkspace();
  return (
    <KalenderSeite key={ws.link.talent_id} perspektive="creator" orgId={ws.link.org_id} orgZone={ws.org.timezone} eigeneTalentId={ws.link.talent_id}
      merkKey={kalenderMerkKey(ws.link.talent_id)} hinweis={null} seesAll={false} />
  );
}

function KalenderSeite({ perspektive, orgId, orgZone, eigeneTalentId, merkKey, hinweis, seesAll, einzeln = false }: {
  perspektive: Perspektive;
  orgId: string;
  orgZone: string;
  eigeneTalentId: string | null;
  merkKey: string;
  hinweis: string | null;
  seesAll: boolean;
  /** Creator-Konto (0178): nur das eigene Profil, ohne Creator-Filter */
  einzeln?: boolean;
}) {
  const agentur = perspektive === "agentur";
  // Creator-Portal auf Englisch (Befund 9); das Agentur-Portal ist deutsch
  const lang: Lang = useLang();
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const [params, setParams] = useSearchParams();
  // Eine Zone für alles, was die Person sieht (Befund 12): eigene Wahl, sonst Browser, sonst Agentur
  const zone = useViewerZone(orgZone);
  const heute = todayIn(zone);
  const schmal = useSchmal();
  const datumParam = params.get("datum");
  const gewaehlt = datumParam && isIsoDate(datumParam) ? datumParam : null;
  const monatParam = params.get("monat");
  const monat = monatParam && isMonthKey(monatParam) ? monatParam : (gewaehlt ?? heute).slice(0, 7);
  // Auf Handy und Tablet passt das Monatsraster nicht (Befund 18, SICHT-AGENTUR-REST-24): dort ist die Liste Standard, der Monat bleibt wählbar
  const ansicht = params.get("ansicht") === "liste" || (schmal && params.get("ansicht") !== "monat") ? "liste" : "monat";
  const art = (ART_FILTER.some((f) => f.key === params.get("art")) ? params.get("art") : "alle") as ArtFilter;
  const talentFilter = agentur ? params.get("creator") ?? "" : "";
  const ausgeblendete = agentur && params.get("ausgeblendet") === "1";
  const terminParam = params.get("termin");
  const setze = (werte: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(werte)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  const bereich = abfrageBereich(monat);
  const q = useQuery({
    queryKey: ["calendar_events", orgId, perspektive, bereich.von, bereich.bis],
    queryFn: () => ladeKalender(api, { orgId, perspektive, talentId: eigeneTalentId, ...bereich }),
    placeholderData: keepPreviousData,
  });
  // Sprung aus Glocke oder Mail (?termin=<id>) ohne Monat: erst das Datum des Termins holen
  const sprung = useQuery({
    queryKey: ["calendar_events", orgId, "sprung", terminParam],
    enabled: !!terminParam && !monatParam,
    queryFn: () => ladeTerminDatum(api, orgId, terminParam!),
  });
  useEffect(() => {
    if (sprung.data && !monatParam) setze({ monat: sprung.data.slice(0, 7), datum: sprung.data });
    // setze ändert sich mit jeder Suche; nur auf das geladene Datum reagieren
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprung.data, monatParam]);

  // Neu seit dem letzten Besuch (TA-125): Zeitpunkt des vorigen Besuchs merken, dann den jetzigen speichern
  const [seit] = useState(() => gespeichert(merkKey));
  useEffect(() => speichere(merkKey, new Date().toISOString()), [merkKey]);

  const [dialog, setDialog] = useState<DialogZustand | null>(null);
  const [abo, setAbo] = useState(false);
  const daten = q.data;
  const n = useMemo(() => (daten ? nachschlag(daten) : null), [daten]);
  const alle = useMemo(() => {
    if (!daten || !n) return [] as Eintrag[];
    return fasseZusammen(baueEintraege({
      feed: daten.feed, termine: daten.termine, perspektive, eigeneTalentId, region: daten.region, jahre: jahreFuer(monat), lang,
      talentIds: new Set(daten.talents.map((t) => t.id)),
      linkIds: new Set([...daten.invoices.map((i) => i.id), ...daten.deals.map((d) => d.id), ...daten.perioden.map((p) => p.id)]),
      beitraege: daten.beitraege,
    }), n);
  }, [daten, n, perspektive, eigeneTalentId, monat, lang]);
  const gruppen = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const x of daten?.mitglieder ?? []) m.set(x.group_id, [...(m.get(x.group_id) ?? []), x.talent_id]);
    return m;
  }, [daten]);
  const gefiltert = useMemo(() => filtere(alle, { talentId: talentFilter, art, ausgeblendete }, gruppen), [alle, talentFilter, art, ausgeblendete, gruppen]);
  const neu = useMemo(() => neuSeit(daten?.termine ?? [], seit, perspektive), [daten, seit, perspektive]);

  // Termin aus dem Sprunglink öffnen, sobald er geladen ist (einmal)
  const geoeffnet = useRef<string | null>(null);
  useEffect(() => {
    if (!terminParam || geoeffnet.current === terminParam) return;
    const e = alle.find((x) => x.key === terminParam);
    if (e) {
      geoeffnet.current = terminParam;
      setDialog({ art: "detail", e });
    }
  }, [alle, terminParam]);

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (!daten || !n) return <Loading />;

  const k: KalenderKontext = { perspektive, orgId, zone, orgZone, heute, n, daten, neu, eigeneTalentId, lang, seesAll };
  const imMonatListe = imMonat(gefiltert, monat, zone);
  const raster = monatsRaster(monat);
  const naechste = anstehend(gefiltert, heute, addDays(heute, 13), 6, zone);
  const tagesListe = gewaehlt && gewaehlt.slice(0, 7) === monat ? amTag(gefiltert, gewaehlt, zone) : null;
  // Sprung aus einer Mail zu einem Termin, den es nicht mehr gibt (Absage, Befund 8)
  const abgesagt = !!terminParam && sprung.isSuccess && sprung.data === null && !alle.some((x) => x.key === terminParam);
  const versteckt = alle.filter((e) => e.ausgeblendet).length;
  const neuImFeed = alle.filter((e) => neu.has(e.key)).length;
  const oeffne = (e: Eintrag) => setDialog({ art: "detail", e });
  const neuerTermin = (datum?: string) => setDialog({ art: "termin", e: null, datum: datum ?? (gewaehlt && gewaehlt.slice(0, 7) === monat ? gewaehlt : monat === heute.slice(0, 7) ? heute : `${monat}-01`) });
  const waehleTag = (tag: string) => setze({ datum: tag === gewaehlt ? null : tag });
  const gefilterterZustand = !!talentFilter || art !== "alle";

  const text = neuImFeed > 0
    ? (lang === "en"
      ? `${neuImFeed === 1 ? "1 new date" : `${neuImFeed} new dates`} ${agentur ? "from creators" : "from your agency"} since your last visit.`
      : `${neuImFeed === 1 ? "1 neuer Termin" : `${neuImFeed} neue Termine`} ${agentur ? "von Creatorn" : "von deiner Agentur"} seit deinem letzten Besuch.`)
    : agentur ? ta("Termine, Fristen und Feiertage für Team und Creator.", "Appointments, deadlines and holidays for the team and creators.", "Deine Termine, Fristen und Feiertage.", "Your dates, deadlines and holidays.") : t("Deine Termine, Fristen und Feiertage.", "Your dates, deadlines and holidays.");
  const wochentage = lang === "en" ? WOCHENTAGE_EN : WOCHENTAGE;

  return (
    <main id="main" className="content tk-kalender">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{agentur ? ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account") : t("Dein Creator-Portal", "Your creator portal")}</div>
          <h1>{agentur ? t("Kalender", "Calendar") : t("Dein Kalender", "Your calendar")}</h1>
          <p role={neuImFeed > 0 ? "status" : undefined}>{text}</p>
        </div>
        <div className="row">
          <button type="button" onClick={() => setAbo(true)}>{t("Kalender abonnieren", "Subscribe to calendar")}</button>
          {agentur && <button type="button" onClick={() => setDialog({ art: "gruppen" })}>{t("Gruppen", "Groups")}</button>}
          <button type="button" className="primary" onClick={() => neuerTermin()}>{agentur ? t("Termin anlegen", "Create appointment") : t("Eigenen Termin anlegen", "Add your own date")}</button>
        </div>
      </div>

      {hinweis && <div className="notice">{hinweis}</div>}
      {abgesagt && <div className="notice" role="status">{t("Dieser Termin wurde abgesagt oder gelöscht und steht nicht mehr im Kalender.", "This date was cancelled or deleted and is no longer in the calendar.")}</div>}
      {zoneHint(zone, orgZone) && <p className="muted">{lang === "en" ? `Times in your time zone (${zone}); the agency works in ${orgZone}.` : zoneHint(zone, orgZone)}</p>}

      {/* Revision 5: Monatsleiste wie im Original (Pfeile quadratisch, Monat | Liste als zusammenhängender Umschalter) */}
      <div className="row between tk-kal-leiste">
        <h2>{monatTitel(monat, lang)}</h2>
        <div className="row">
          <button type="button" aria-label={t("Voriger Monat", "Previous month")} onClick={() => setze({ monat: addMonths(monat, -1), datum: null, termin: null })}>‹</button>
          <button type="button" onClick={() => setze({ monat: null, datum: heute, termin: null })}>{t("Heute", "Today")}</button>
          <button type="button" aria-label={t("Nächster Monat", "Next month")} onClick={() => setze({ monat: addMonths(monat, 1), datum: null, termin: null })}>›</button>
          <button type="button" className={ansicht === "monat" ? "primary" : ""} aria-pressed={ansicht === "monat"} onClick={() => setze({ ansicht: schmal ? "monat" : null })}>{t("Monat", "Month")}</button>
          <button type="button" className={ansicht === "liste" ? "primary" : ""} aria-pressed={ansicht === "liste"} onClick={() => setze({ ansicht: "liste" })}>{t("Liste", "List")}</button>
        </div>
      </div>

      <div className="toolbar" role="group" aria-label="Filter">
        {ART_FILTER.map((f) => (
          <button key={f.key} type="button" aria-pressed={art === f.key} onClick={() => setze({ art: f.key === "alle" ? null : f.key })}>
            {lang === "en" ? f.label_en : f.label}
          </button>
        ))}
        {agentur && !einzeln && (
          <select aria-label={t("Nach Creator filtern", "Filter by creator")} value={talentFilter} onChange={(e) => setze({ creator: e.target.value || null })}>
            <option value="">{t("Alle Creator", "All creators")}</option>
            <option value="team">{t("Nur Team (intern)", "Team only (internal)")}</option>
            {daten.talents.map((tl) => <option key={tl.id} value={tl.id}>{talentVoll(tl)}{tl.status === "archived" ? t(" (archiviert)", " (archived)") : ""}</option>)}
          </select>
        )}
        {agentur && (
          <button type="button" aria-pressed={ausgeblendete} onClick={() => setze({ ausgeblendet: ausgeblendete ? null : "1" })}>
            {t("Ausgeblendete zeigen", "Show hidden")}{versteckt > 0 ? ` · ${versteckt}` : ""}
          </button>
        )}
        {!agentur && eigeneTalentId && (
          <FeiertagsRegion talentId={eigeneTalentId} eigene={daten.eigeneRegion} agentur={daten.agenturRegion} lang={lang} />
        )}
      </div>

      {ansicht === "monat" ? (
        <div className="calendar" role="group" aria-label={monatTitel(monat, lang)} aria-busy={q.isFetching || undefined}>
          {wochentage.map((w) => <div key={w} className="day tk-cal-kopf"><strong>{w}</strong></div>)}
          {Array.from({ length: raster.leer }, (_, i) => <div key={`leer-${i}`} className="day"><strong></strong></div>)}
          {raster.tage.map((tag) => {
            const liste = amTag(imMonatListe, tag, zone);
            const zeigen = liste.slice(0, 4);
            const mehr = liste.length - zeigen.length;
            const cls = `day${tag === heute ? " tk-cal-heute" : ""}${tag === gewaehlt ? " tk-cal-gewaehlt" : ""}`;
            return (
              <div key={tag} className={cls}>
                <strong>
                  <button type="button" className="tk-cal-tag" aria-pressed={tag === gewaehlt} onClick={() => waehleTag(tag)}
                    aria-label={`${tagLang(tag, lang)}${tag === heute ? t(" (heute)", " (today)") : ""}${liste.length ? ` · ${liste.length === 1 ? t("1 Eintrag", "1 entry") : t(`${liste.length} Einträge`, `${liste.length} entries`)}` : ""}`}>
                    {Number(tag.slice(8, 10))}
                  </button>
                </strong>
                {zeigen.map((e) => <EintragKnopf key={`${e.key}-${tag}`} e={e} tag={tag} k={k} onOpen={oeffne} />)}
                {mehr > 0 && <button type="button" className="tk-cal-mehr" onClick={() => setze({ datum: tag })}>+{mehr} {t("weitere", "more")}</button>}
              </div>
            );
          })}
        </div>
      ) : (
        <Liste eintraege={imMonatListe} monat={monat} k={k} onOpen={oeffne} gefiltert={gefilterterZustand} />
      )}

      <div className="notice" aria-live="polite">
        {naechste.length ? (
          naechste.map((e, i) => (
            <span key={e.key}>
              {i > 0 && <br />}
              {hinweisZeile(e, perspektive, n, zone, lang)}
            </span>
          ))
        ) : (
          <>{t("Keine Termine und Fristen in den nächsten 14 Tagen", "No dates or deadlines in the next 14 days")}{gefilterterZustand ? t(" für diese Auswahl", " for this selection") : ""}.</>
        )}
      </div>

      {tagesListe && gewaehlt && (
        <section className="panel stack" aria-labelledby="kalender-tag">
          <div className="row between">
            <h3 id="kalender-tag">{tagLang(gewaehlt, lang)}</h3>
            <div className="row">
              <button type="button" onClick={() => neuerTermin(gewaehlt)}>{t("Termin an diesem Tag", "Date on this day")}</button>
              <button type="button" onClick={() => setze({ datum: null })}>{t("Schließen", "Close")}</button>
            </div>
          </div>
          {tagesListe.length ? tagesListe.map((e) => <EintragZeile key={e.key} e={e} k={k} onOpen={oeffne} />) : <p>{t("An diesem Tag gibt es keine Einträge.", "There are no entries on this day.")}</p>}
        </section>
      )}

      <DemoHinweis lang={k.lang} />
      <KalenderDialoge zustand={dialog} setZustand={setDialog} k={k} />
      <Dialog open={abo} onClose={() => setAbo(false)} title={t("Kalender abonnieren", "Subscribe to calendar")}>
        {abo && <Abo orgId={orgId} lang={lang} />}
      </Dialog>
    </main>
  );
}

/**
 * Bis zu dieser Fensterbreite ist die Liste Standard (SICHT-AGENTUR-REST-24): Zwischen Handy und etwa 1000 px sind die
 * Tageszellen neben der Seitenleiste zu schmal für Art und Name eines Eintrags („Auszah…“). Der Monat bleibt wählbar.
 */
export const LISTE_STANDARD_BIS = "(max-width: 1000px)";
/** Schmaler Bildschirm (Handy, Tablet): Liste statt Monatsraster. */
function useSchmal(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => undefined;
      const mq = window.matchMedia(LISTE_STANDARD_BIS);
      mq.addEventListener("change", cb);
      return () => mq.removeEventListener("change", cb);
    },
    () => (typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(LISTE_STANDARD_BIS).matches),
    () => false,
  );
}

/** Adresse der Abo-Funktion (Edge Function calendar-ics); in der Demo gibt es keinen Server dafür. */
function aboAdresse(token: string): string | null {
  const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  return base ? `${base.replace(/\/+$/, "")}/functions/v1/calendar-ics?t=${token}` : null;
}

/**
 * Kalender-Abo (Befund 18): geheimer Link für Google, Apple oder Outlook. Er zeigt genau das, was du hier siehst,
 * und lässt sich jederzeit neu erzeugen (der alte wird ungültig) oder beenden.
 */
function Abo({ orgId, lang }: { orgId: string; lang: Lang }) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const fmt = (d: string) => (lang === "en" ? dateOf(d, "en") : date(d));
  const userId = api.session()?.userId ?? "";
  const stand = useQuery({ queryKey: ["calendar_subscriptions", userId, orgId], queryFn: () => aboStand(api, userId, orgId) });
  const { busy, run } = useAction();
  const [token, setToken] = useState<string | null>(null);
  const url = token ? aboAdresse(token) : null;
  const erzeugen = () => void run("abo", async () => setToken(await aboErzeugen(api, orgId)), stand.data ? t("Neuer Link erzeugt. Der alte gilt nicht mehr.", "New link created. The old one no longer works.") : t("Link erzeugt.", "Link created."));
  return (
    <div className="stack">
      <p>{t("Mit dem Abo erscheinen deine Termine und Fristen in Google Kalender, Apple Kalender oder Outlook, mit Ort und Beschreibung. Der Link ist geheim und nur für dich; wer ihn kennt, sieht deinen Kalender.",
        "With the subscription your dates and deadlines appear in Google Calendar, Apple Calendar or Outlook, including location and description. The link is secret and only for you; anyone who has it can see your calendar.")}</p>
      {stand.data && !token && (
        <p className="muted">{t("Abo aktiv seit", "Subscription active since")} {fmt(stand.data.created_at)}{stand.data.last_used_at ? t(`, zuletzt abgerufen am ${fmt(stand.data.last_used_at)}`, `, last fetched on ${fmt(stand.data.last_used_at)}`) : t(", noch nicht abgerufen", ", not fetched yet")}.</p>
      )}
      {token && (url ? (
        <label>
          {t("Abo-Link", "Subscription link")}
          <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
          <small>{t("Im Kalenderprogramm „Kalender abonnieren“ bzw. „Per URL hinzufügen“ wählen und den Link einfügen. Er wird nur jetzt angezeigt.",
            "In your calendar app choose “Subscribe to calendar” or “Add by URL” and paste the link. It is only shown now.")}</small>
        </label>
      ) : (
        <p className="notice">{t("Demo: Der Link ist erzeugt, aber in der Demo gibt es keinen Abo-Server. Im Betrieb steht hier die Adresse zum Einfügen.",
          "Demo: the link was created, but the demo has no subscription server. In production the address to paste appears here.")}</p>
      ))}
      <div className="row">
        <button type="button" className="primary" disabled={!!busy} onClick={erzeugen}>{stand.data ? t("Neuen Link erzeugen", "Create new link") : t("Link erzeugen", "Create link")}</button>
        {url && <button type="button" onClick={() => void navigator.clipboard?.writeText(url)}>{t("Link kopieren", "Copy link")}</button>}
        {stand.data && (
          <button type="button" disabled={!!busy} onClick={() => void run("abo-ende", async () => { await aboBeenden(api, orgId); setToken(null); }, t("Abo beendet. Der Link gilt nicht mehr.", "Subscription ended. The link no longer works."))}>{t("Abo beenden", "End subscription")}</button>
        )}
      </div>
    </div>
  );
}

/** Creator: Feiertage der eigenen Region statt der Agentur-Region (Befund 18); alle Länder und Kantone (Befund 13). */
function FeiertagsRegion({ talentId, eigene, agentur, lang }: { talentId: string; eigene: string | null; agentur: string | null; lang: Lang }) {
  const { busy, run } = useAction();
  const en = lang === "en";
  const name = (r: { label: string; label_en: string }) => (en ? r.label_en : r.label);
  const agenturRegion = HOLIDAY_REGIONS.find((r) => r.key === agentur);
  const agenturName = agenturRegion ? name(agenturRegion) : en ? "none" : "keine";
  return (
    <select className="tk-select-fit" aria-label={en ? "Holidays" : "Feiertage"} value={eigene ?? ""} disabled={!!busy}
      onChange={(e) => void run("region", () => speichereFeiertagsregion(api, talentId, e.target.value || null), en ? "Holidays updated." : "Feiertage angepasst.")}>
      <option value="">{en ? `Holidays as your agency (${agenturName})` : `Feiertage wie die Agentur (${agenturName})`}</option>
      {HOLIDAY_REGIONS.map((r) => <option key={r.key} value={r.key}>{en ? "Holidays" : "Feiertage"}: {name(r)}</option>)}
    </select>
  );
}

function EintragKnopf({ e, tag, k, onOpen }: { e: Eintrag; tag: string; k: KalenderKontext; onOpen: (e: Eintrag) => void }) {
  const p = pilleFuer(e, k.perspektive, k.n, k.heute, k.zone, k.lang);
  const zeichen = e.art === "event" ? "" : `${p.symbol} `;
  // Mehrtägige Termine: an den Folgetagen „→ Titel“ wie im bisherigen Portal
  const label = e.ende && tag > lokaleTage(e, k.zone).beginn ? `→ ${e.titel}` : p.label;
  const en = k.lang === "en";
  return (
    <button type="button" className={`tk-cal-eintrag${e.ausgeblendet ? " tk-cal-ausgeblendet" : ""}`} onClick={() => onOpen(e)} title={`${zeichen}${label}`}
      aria-label={`${eintragTitel(e, k.n, k.lang, k.perspektive)}${e.ausgeblendet ? t(" (ausgeblendet)", " (hidden)") : ""}${k.neu.has(e.key) ? (en ? " (new)" : " (neu)") : ""} · ${en ? "Open details" : "Details öffnen"}`}>
      <span className={`pill ${p.tone}`}>
        {e.art !== "holiday" && <i className="tk-cal-punkt" style={{ background: farbWert(e.farbe) }} aria-hidden="true" />}
        {zeichen}{label}
      </span>
    </button>
  );
}

function EintragZeile({ e, k, onOpen }: { e: Eintrag; k: KalenderKontext; onOpen: (e: Eintrag) => void }) {
  const en = k.lang === "en";
  const zeit = zeitText(e, k.zone, k.lang);
  const wer = verantwortlich(e, k.perspektive, k.n, k.lang);
  // Zusagen (0056): die Agentur sieht den Stand, der Creator seine eigene Antwort
  const antworten = e.termin ? k.daten.antworten.filter((a) => a.event_id === e.termin!.id) : [];
  const rsvp = k.perspektive === "agentur" && antworten.length
    ? t(`${antworten.filter((a) => a.response === "accepted").length} zugesagt${antworten.some((a) => a.response !== "accepted") ? t(`, ${antworten.filter((a) => a.response !== "accepted").length} abgesagt oder mit Rückfrage`, `, ${antworten.filter((a) => a.response !== "accepted").length} declined or with a question`) : ""}`, `${antworten.filter((a) => a.response === "accepted").length} accepted${antworten.some((a) => a.response !== "accepted") ? t(`, ${antworten.filter((a) => a.response !== "accepted").length} abgesagt oder mit Rückfrage`, `, ${antworten.filter((a) => a.response !== "accepted").length} declined or with a question`) : ""}`)
    : null;
  const eigene = k.perspektive === "creator" && e.termin && e.termin.created_by_side === "agency" ? antworten.find((a) => a.talent_id === k.eigeneTalentId) : undefined;
  const teile = [zeit.text, zielgruppeText(e, k.perspektive, k.n, k.lang), wer ? `${en ? "Responsible" : "Verantwortlich"}: ${wer.charAt(0).toUpperCase()}${wer.slice(1)}` : null, rsvp].filter(Boolean);
  return (
    <div className={`task${e.ausgeblendet ? " tk-cal-ausgeblendet" : ""}`}>
      <div>
        <strong>
          <button type="button" className="tk-cal-link" onClick={() => onOpen(e)} title={eintragTitel(e, k.n, k.lang, k.perspektive)}>
            {e.art !== "holiday" && <i className="tk-cal-punkt" style={{ background: farbWert(e.farbe) }} aria-hidden="true" />}
            {eintragTitel(e, k.n, k.lang, k.perspektive)}
          </button>
        </strong>
        <p>{teile.join(" · ")}{e.ende ? ` · ${en ? "until" : "bis"} ${tagLang(e.ende, k.lang)}` : ""}</p>
      </div>
      <div className="row">
        {eigene && <PillView pill={antwortPille(eigene.response, k.lang)} />}
        <PillView pill={listenPille(e, k.heute, k.neu.has(e.key), k.lang, k.n)} />
      </div>
    </div>
  );
}

/** Listenansicht des Monats (AD-187, TA-120): nach Tagen gruppiert, mehrtägige Termine an ihrem ersten Tag im Monat. */
function Liste({ eintraege, monat, k, onOpen, gefiltert }: { eintraege: Eintrag[]; monat: string; k: KalenderKontext; onOpen: (e: Eintrag) => void; gefiltert: boolean }) {
  const tage = new Map<string, Eintrag[]>();
  const en = k.lang === "en";
  for (const e of eintraege) {
    const beginn = lokaleTage(e, k.zone).beginn;
    const tag = beginn < `${monat}-01` ? `${monat}-01` : beginn;
    tage.set(tag, [...(tage.get(tag) ?? []), e]);
  }
  const sortiert = [...tage.entries()].sort(([a], [b]) => a.localeCompare(b));
  return (
    <section className="panel stack" aria-label={`${en ? "List" : "Liste"} ${monatTitel(monat, k.lang)}`}>
      {sortiert.map(([tag, liste]) => (
        <div key={tag}>
          <div className="group-label">{tagLang(tag, k.lang)}{tag === k.heute ? (en ? " · today" : " · heute") : ""}</div>
          {liste.map((e) => <EintragZeile key={e.key} e={e} k={k} onOpen={onOpen} />)}
        </div>
      ))}
      {sortiert.length === 0 && (
        gefiltert
          ? <p>{en ? "No matching results. Adjust the filters." : "Keine passenden Ergebnisse. Passe die Filter an."}</p>
          : <p>{en ? "There are no entries this month." : "In diesem Monat gibt es keine Einträge."}</p>
      )}
    </section>
  );
}
